'use client';

import Image from 'next/image';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';

export interface UseCaseCard {
  id: string;
  title: string;
  description: string;
  image: string;
  /** 卡片单独跳转地址，缺省时使用底部按钮的 redirectUrl */
  link?: string;
}

interface UseCaseCardsProps {
  title: string;
  subtitle: string;
  cards: UseCaseCard[];
  buttonText: string;
  redirectUrl: string;
  /** 埋点所在页面 */
  page: string;
}

const ArrowRight = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" fill="none" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M23.5857 15.7071L17.2928 9.41421L18.707 8L27.4142 16.7071L18.707 25.4142L17.2928 24L23.5857 17.7071H6L6 15.7071H23.5857Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * 使用场景卡片网格：H2 标题 + 副标题 + 3 列 4:3 配图卡片（hover 显示箭头）+ 底部居中 CTA。
 */
export function UseCaseCards({ title, subtitle, cards, buttonText, redirectUrl, page }: UseCaseCardsProps) {
  const router = useRouter();

  const goTo = (url: string) => {
    reportEvent('element_click', { element_name: 'generate-now', page });
    router.push(url);
  };

  return (
    <section className="w-full bg-black pt-28 sm:pt-12">
      <div className="mx-auto max-w-[1440px] px-8 md:px-4">
        <div className="w-full text-center">
          <h2 className="font-semibold text-[40px] text-white leading-tight sm:text-[32px]">{title}</h2>
          <p className="mx-auto mt-4 max-w-3xl font-normal text-white/60 text-xl sm:text-base">{subtitle}</p>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-8 sm:mt-8 sm:grid-cols-1 sm:gap-6">
          {cards.map((card) => (
            <button
              type="button"
              key={card.id}
              className="flex flex-col gap-6 text-left"
              onClick={() => goTo(card.link ?? redirectUrl)}
            >
              <div className="group relative overflow-hidden rounded-2xl bg-white/10">
                <Image
                  className="aspect-[4/3] w-full object-cover"
                  src={card.image}
                  alt={card.title}
                  width={620}
                  height={465}
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-b from-transparent to-black/60 px-5 py-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="flex size-[42px] items-center justify-center rounded-full border border-white/10 bg-black/60 shadow-[0_2px_8px_rgba(0,0,0,0.2)] backdrop-blur-[4px]">
                    <ArrowRight className="size-5 text-white" />
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <h3 className="font-semibold text-white text-xl leading-6">{card.title}</h3>
                <p className="font-normal text-base text-white/60 leading-[1.6] sm:text-sm">{card.description}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 flex justify-center sm:mt-8">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl transition-all hover:shadow-xl sm:px-10 sm:py-5 sm:text-base"
            onClick={() => goTo(redirectUrl)}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </section>
  );
}
